import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import "focus-visible";
import * as React from "react";
import { useEffect, useMemo, useReducer, useRef } from "react";
import { AnalyticsObserver } from "../analytics/AnalyticsObserver";
import { BlackLayer } from "../blackLayer/BlackLayer";
import { Header } from "../Header/Header";
import { translations } from "../i18n";
import { i18nContext } from "../i18n/i18nContext";
import { ILang } from "../i18n/ILang";
import { KeyboardObserver } from "../keyboard/KeyboardObserver";
import { menuWithLang } from "../Menu/data";
import { Menu } from "../Menu/Menu";
import { Search } from "../Search/Search";
import { getGlobalThis, getMobileDevice, normalizeLang } from "../utils";
import { decorateElementsAPI } from "./api";
import { NavContext } from "./NavContext";
import { initialState, NavState, reducer, setUserLoggedIn, setUserLoggedOut } from "./reducer";

export type Props = {
  lang?: NavState["lang"];
  user?: NavState["user"];
  hasAdminBar?: NavState["hasAdminBar"];
  shoppingListNumItems?: NavState["shoppingListNumItems"];
  onLoginRequestCallback?: NavState["onLoginRequestCallback"];
  onLogoutRequestCallback?: NavState["onLogoutRequestCallback"];
  onAnalyticsRequestCallback?: NavState["onAnalyticsRequestCallback"];
};

export function Nav({
  lang,
  user,
  hasAdminBar,
  shoppingListNumItems,
  onLoginRequestCallback,
  onLogoutRequestCallback,
  onAnalyticsRequestCallback
}: Props) {
  const elRef = useRef<HTMLDivElement>();
  const containerRef = useRef<HTMLDivElement>();
  const normalizedLang = normalizeLang(ILang.en, lang);
  const normalizedUser = user ?? initialState.user;

  const [state, dispatch] = useReducer(reducer, initialState, s => ({
    ...s,
    lang: normalizedLang,
    user: normalizedUser,
    hasAdminBar,
    shoppingListNumItems: shoppingListNumItems ?? 0,
    onLoginRequestCallback,
    onLogoutRequestCallback,
    onAnalyticsRequestCallback,
    menuItems: menuWithLang(normalizedLang)
  }));

  useEffect(() => {
    dispatch(user ? setUserLoggedIn(user) : setUserLoggedOut());
  }, [user]);

  useEffect(() => {
    if (hasAdminBar === true) {
      const el = containerRef.current;
      el.classList.add("elm-mt-10", "md:elm-mt-7");
    }
  }, [hasAdminBar]);

  getGlobalThis().elements = decorateElementsAPI(dispatch, getGlobalThis().elements);

  const i18n = useMemo(() => translations(state.lang), [state.lang]);
  const { isSearchOpen, isMenuOpen } = state;

  useEffect(() => {
    // disable the scroll in the page when the menu/search are open (only desktop)
    const { isMobile } = getMobileDevice(navigator);

    if (!isMobile) {
      const el = elRef.current;

      if (isMenuOpen || isSearchOpen) {
        disableBodyScroll(el, {
          reserveScrollBarGap: true
        });
      }

      return () => enableBodyScroll(el);
    }
  }, [isSearchOpen, isMenuOpen]);

  return (
    <i18nContext.Provider value={[i18n]}>
      <NavContext.Provider value={[state, dispatch]}>
        <AnalyticsObserver elRef={elRef} lang={state.lang}>
          <KeyboardObserver>
            <div
              data-lang={state.lang}
              ref={elRef}
              className="elements elements--navbar elm-h-14 elm-text-gray-900 elm-text-base"
              data-testid="elements--navbar"
            >
              <div
                data-testid="elements--navbar--container"
                ref={containerRef}
                className="print:elm-hidden elm-subpixel-antialiased elm-border-dd-gray-300 elm-border-0 elm-border-b elm-border-solid elm-font-sans elm-fixed elm-z-1001 elm-top-0 elm-left-0 elm-w-full elm-bg-white"
                style={{ height: "57px" }}
              >
                <BlackLayer />
                <Header />
                {isSearchOpen && <Search />}
                <Menu />
              </div>
            </div>
          </KeyboardObserver>
        </AnalyticsObserver>
      </NavContext.Provider>
    </i18nContext.Provider>
  );
}
